import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import config from '../config';
import defaultCv from '../data/defaultPortfolioCv';
import defaultProjects from '../data/defaultPortfolioProjects.json';
import { getProjectPosterUrl, getProjectVideoUrl, isVideoProject } from '../utils/portfolioMedia';
import HoverVideo from './HoverVideo';
import './PortfolioPage.css';

const API_BASE = config.API_BASE;
const UPLOADS_BASE = config.UPLOADS_BASE || config.API_BASE;
const SLIDE_MS = 320;
const STICK_GAP = 100;

const DEMO_PROJECTS = defaultProjects.map((project, index) => ({
  ...project,
  id: project.slug || `demo-${index + 1}`
}));

function mediaUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `${UPLOADS_BASE}${url}`;
  return url;
}

function getBodyParagraphs(project) {
  const text = project?.body_text || project?.description || '';
  return text
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function normalizeGalleryItem(item, project) {
  return {
    image_url: item.image_url,
    title: item.title || project.title || '',
    client: item.client || item.caption || project.metadata_client || '',
    year: item.year || project.metadata_year || ''
  };
}

function getGalleryItems(project) {
  if (!project) return [];
  let items = project.gallery_items;
  if (typeof items === 'string') {
    try {
      items = JSON.parse(items);
    } catch {
      items = [];
    }
  }
  if (Array.isArray(items) && items.length > 0) {
    return items.filter((item) => item?.image_url).map((item) => normalizeGalleryItem(item, project));
  }
  return [];
}

function ProjectMedia({ project, imageUrl, className }) {
  if (isVideoProject(project)) {
    return (
      <HoverVideo
        src={mediaUrl(getProjectVideoUrl(project))}
        poster={mediaUrl(getProjectPosterUrl(project))}
        alt={project.title}
        className={className}
      />
    );
  }
  return <img src={mediaUrl(imageUrl)} alt={project.title} className={className} loading="lazy" />;
}

function ProjectCard({ project, onView }) {
  return (
    <article className="portfolio-card">
      <button
        type="button"
        className="portfolio-card__media"
        onClick={onView}
        aria-label={`View ${project.title || 'project'}`}
      >
        <ProjectMedia project={project} imageUrl={project.thumbnail_url || project.media_url} />
      </button>
      <div className="portfolio-card__meta">
        <div className="portfolio-card__meta-title">
          <h3>{project.title || 'Project Name'}</h3>
        </div>
        <div className="portfolio-card__meta-details">
          <p>{project.metadata_client || ''}</p>
          <p>{project.metadata_role || ''}</p>
          <p>{project.metadata_year || ''}</p>
          <p className="portfolio-card__desc">{project.description || ''}</p>
          <button className="portfolio-link" onClick={onView} type="button">
            [{project.view_more_label || 'View..'}]
          </button>
        </div>
      </div>
    </article>
  );
}

function PortfolioFooter({ navigate, centerLink = 'index', profileName }) {
  const centerLabel = centerLink === 'projects' ? 'Projects' : 'Index';
  const centerPath = centerLink === 'projects' ? '/portfolio' : '/portfolio/index';
  const footerName = profileName || defaultCv.profile.name;

  return (
    <footer className="portfolio-footer">
      <span>{footerName}</span>
      <button type="button" className="portfolio-link" onClick={() => navigate(centerPath)}>{centerLabel}</button>
      <button type="button" className="portfolio-link footer-right" onClick={() => navigate('/portfolio/information')}>Information</button>
    </footer>
  );
}

function PortfolioTopbar() {
  return (
    <div className="portfolio-topbar">
      <div>Digital</div>
      <div>Physical</div>
    </div>
  );
}

function IndexGridCell({ project, onOpen }) {
  return (
    <button type="button" className="portfolio-index-cell" onClick={() => onOpen(project)}>
      <ProjectMedia project={project} imageUrl={project.thumbnail_url || project.media_url} />
    </button>
  );
}

const INDEX_GRID_STACKS = 4;

function IndexGridColumn({ projects, onOpen }) {
  // Independent stacks so each image sits right under the one above it, not under the row's tallest
  const stacks = Array.from({ length: INDEX_GRID_STACKS }, () => []);
  projects.forEach((project, index) => {
    stacks[index % INDEX_GRID_STACKS].push(project);
  });

  return (
    <div className="portfolio-index-grid">
      {stacks.map((stack, stackIndex) => (
        <div key={`stack-${stackIndex}`} className="portfolio-index-grid__stack">
          {stack.map((project) => (
            <IndexGridCell
              key={project._id || project.id || project.slug}
              project={project}
              onOpen={onOpen}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function PortfolioStatusMessage({ loading, loadError, isEmpty }) {
  if (loading) {
    return <p className="portfolio-status">Loading projects…</p>;
  }
  if (loadError) {
    return <p className="portfolio-status portfolio-status--error">{loadError}</p>;
  }
  if (isEmpty) {
    return (
      <p className="portfolio-status">
        No projects published yet. Add projects in Admin → Portfolio.
      </p>
    );
  }
  return null;
}

function PortfolioMain({ projects, profileName, loading, loadError }) {
  const navigate = useNavigate();
  const { leftProjects, rightProjects } = useMemo(() => {
    const published = (projects || [])
      .filter((p) => p.published !== false)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    return {
      leftProjects: published.filter((p) => p.column !== 'right'),
      rightProjects: published.filter((p) => p.column === 'right')
    };
  }, [projects]);
  const isEmpty = !loading && !loadError && leftProjects.length === 0 && rightProjects.length === 0;

  return (
    <div className="portfolio-page">
      <div className="portfolio-shell">
        <PortfolioTopbar />
        <PortfolioStatusMessage loading={loading} loadError={loadError} isEmpty={isEmpty} />
        <div className="portfolio-columns">
          <div className="portfolio-column portfolio-column--left">
            {leftProjects.map((project) => (
              <ProjectCard
                key={project._id || project.id}
                project={project}
                onView={() => navigate(`/portfolio/project/${project.slug}`)}
              />
            ))}
          </div>
          <div className="portfolio-column portfolio-column--right">
            {rightProjects.map((project) => (
              <ProjectCard
                key={project._id || project.id}
                project={project}
                onView={() => navigate(`/portfolio/project/${project.slug}`)}
              />
            ))}
          </div>
        </div>
        <PortfolioFooter navigate={navigate} profileName={profileName} />
      </div>
    </div>
  );
}

function PortfolioIndexView({ projects, profileName, loading, loadError }) {
  const navigate = useNavigate();
  const { leftProjects, rightProjects } = useMemo(() => {
    const published = (projects || [])
      .filter((p) => p.published !== false)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    return {
      leftProjects: published.filter((p) => p.column !== 'right'),
      rightProjects: published.filter((p) => p.column === 'right')
    };
  }, [projects]);
  const isEmpty = !loading && !loadError && leftProjects.length === 0 && rightProjects.length === 0;

  const openProject = (project) => {
    navigate(`/portfolio/project/${project.slug}`, { state: { from: 'index' } });
  };

  return (
    <div className="portfolio-page portfolio-page--index">
      <div className="portfolio-shell portfolio-shell--index">
        <div className="portfolio-index-body-wrap">
          <PortfolioTopbar />
          <PortfolioStatusMessage loading={loading} loadError={loadError} isEmpty={isEmpty} />
          <div className="portfolio-columns portfolio-columns--index">
            <div className="portfolio-column portfolio-column--left">
              <IndexGridColumn projects={leftProjects} onOpen={openProject} />
            </div>
            <div className="portfolio-column portfolio-column--right">
              <IndexGridColumn projects={rightProjects} onOpen={openProject} />
            </div>
          </div>
        </div>
        <PortfolioFooter navigate={navigate} centerLink="projects" profileName={profileName} />
      </div>
    </div>
  );
}

function ProjectSlidePanel({ projects }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const project = projects.find((p) => p.slug === slug);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const panelRef = useRef(null);
  const featuredRef = useRef(null);
  const featuredColRef = useRef(null);
  const pinScrollTopRef = useRef(null);

  useEffect(() => {
    setClosing(false);
    setOpen(false);
    pinScrollTopRef.current = null;
    const frame = window.requestAnimationFrame(() => {
      setOpen(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [slug]);

  useEffect(() => {
    const panel = panelRef.current;
    const featured = featuredRef.current;
    if (!panel || !featured) return undefined;

    let lastAppliedOffset = null;

    const calibratePinScrollTop = () => {
      featured.style.transform = '';
      const panelRect = panel.getBoundingClientRect();
      const featuredRect = featured.getBoundingClientRect();
      const targetBottom = panelRect.bottom - STICK_GAP;
      return panel.scrollTop + featuredRect.bottom - targetBottom;
    };

    const applyScrollPin = () => {
      const pinScrollTop = pinScrollTopRef.current;
      if (pinScrollTop === null) return;

      const offset = Math.max(0, panel.scrollTop - pinScrollTop);
      if (lastAppliedOffset !== null && Math.abs(offset - lastAppliedOffset) < 0.5) return;

      lastAppliedOffset = offset;
      featured.style.transform = offset > 0 ? `translate3d(0, ${offset}px, 0)` : '';
    };

    const refreshLayout = () => {
      lastAppliedOffset = null;
      pinScrollTopRef.current = calibratePinScrollTop();
      applyScrollPin();
    };

    refreshLayout();

    panel.addEventListener('scroll', applyScrollPin, { passive: true });
    window.addEventListener('resize', refreshLayout);

    featured.querySelectorAll('img, video').forEach((media) => {
      media.addEventListener('load', refreshLayout);
    });

    return () => {
      panel.removeEventListener('scroll', applyScrollPin);
      window.removeEventListener('resize', refreshLayout);
      featured.style.transform = '';
      pinScrollTopRef.current = null;
    };
  }, [slug, open, project?.body_text, project?.description, project?.media_url]);

  const side = project?.column === 'right' ? 'right' : 'left';

  const handleClose = () => {
    setClosing(true);
    setOpen(false);
    window.setTimeout(() => {
      setClosing(false);
      const fromIndex = location.state?.from === 'index';
      navigate(fromIndex ? '/portfolio/index' : '/portfolio');
    }, SLIDE_MS);
  };

  if (!project) {
    return null;
  }

  const galleryItems = getGalleryItems(project);
  const panelTitle = project.subtitle || project.title;
  const panelSubtitle = project.metadata_role || '';
  const bodyParagraphs = getBodyParagraphs(project);

  return (
    <>
      <button
        type="button"
        className={`portfolio-slide-backdrop${open && !closing ? ' is-visible' : ''}`}
        aria-label="Close project"
        onClick={handleClose}
      />
      <aside
        ref={panelRef}
        className={`portfolio-slide-panel portfolio-slide-panel--${side}${open ? ' is-open' : ''}`}
        aria-hidden={!open}
      >
        <div className="portfolio-slide-panel__inner">
          <div className="portfolio-slide-panel__header">
            <button className="portfolio-link portfolio-slide-panel__close" onClick={handleClose} type="button">
              ← Close Project
            </button>
            <div className="portfolio-slide-panel__header-info">
              <h2>{panelTitle}</h2>
              {panelSubtitle && <p>{panelSubtitle}</p>}
            </div>
          </div>
          <div className="portfolio-slide-panel__content">
            <div className="portfolio-slide-panel__gallery">
              {galleryItems.map((item, index) => (
                <figure key={`${project.slug}-gallery-${index}`} className="portfolio-slide-panel__gallery-item">
                  <img src={mediaUrl(item.image_url)} alt={item.title || project.title} loading="lazy" />
                  <figcaption className="portfolio-slide-panel__gallery-caption">
                    {item.title && <span className="portfolio-slide-panel__gallery-line">{item.title}</span>}
                    {item.client && <span className="portfolio-slide-panel__gallery-line portfolio-slide-panel__gallery-line--client">{item.client}</span>}
                    {item.year && <span className="portfolio-slide-panel__gallery-line">{item.year}</span>}
                  </figcaption>
                </figure>
              ))}
            </div>
            <div ref={featuredColRef} className="portfolio-slide-panel__featured-col">
              <figure ref={featuredRef} className="portfolio-slide-panel__featured">
              <ProjectMedia
                project={project}
                imageUrl={project.media_url}
                className="portfolio-slide-panel__featured-media"
              />
              <figcaption className="portfolio-slide-panel__featured-caption">
                <div className="portfolio-slide-panel__gallery-caption">
                  {project.title && <span className="portfolio-slide-panel__gallery-line">{project.title}</span>}
                  {project.metadata_client && <span className="portfolio-slide-panel__gallery-line portfolio-slide-panel__gallery-line--client">{project.metadata_client}</span>}
                  {project.metadata_year && <span className="portfolio-slide-panel__gallery-line">{project.metadata_year}</span>}
                </div>
                {bodyParagraphs.length > 0 && (
                  <div className="portfolio-slide-panel__featured-body">
                    {bodyParagraphs.map((paragraph, index) => (
                      <p key={`${project.slug}-body-${index}`}>{paragraph}</p>
                    ))}
                  </div>
                )}
              </figcaption>
            </figure>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function PortfolioInfoContact({ item }) {
  if (item.href) {
    return (
      <a className="portfolio-info-contact" href={item.href}>
        {item.text}
      </a>
    );
  }

  return <span className="portfolio-info-contact">{item.text}</span>;
}

function PortfolioInfoEntries({ entries }) {
  return entries.map((entry, index) => (
    <div key={`entry-${index}`} className="portfolio-info-entry">
      {entry.title && <p className="portfolio-info-entry__title">{entry.title}</p>}
      {entry.subtitle && <p>{entry.subtitle}</p>}
      {entry.meta && <p>{entry.meta}</p>}
      {entry.date && <p>{entry.date}</p>}
      {entry.details?.map((line, detailIndex) => (
        <p key={`detail-${detailIndex}`} className="portfolio-info-entry__detail" style={{ whiteSpace: 'pre-line' }}>
          {line}
        </p>
      ))}
    </div>
  ));
}

function PortfolioInfoSectionContent({ section }) {
  if (section.type === 'list') {
    return (
      <>
        {section.items.map((item) => (
          <p key={item} className="portfolio-info-list-item">
            {item}
          </p>
        ))}
        {section.footer && <p className="portfolio-info-list-footer">{section.footer}</p>}
      </>
    );
  }

  return <PortfolioInfoEntries entries={section.entries || []} />;
}

function hasSavedCv(doc) {
  return Boolean(doc?.profile && Array.isArray(doc?.sections));
}

function PortfolioInfo({ cv: cvProp }) {
  const navigate = useNavigate();
  const cv = hasSavedCv(cvProp) ? cvProp : defaultCv;
  const footerName = cv.profile?.name || defaultCv.profile.name;

  return (
    <div className="portfolio-page portfolio-page--info">
      <div className="portfolio-shell portfolio-shell--info">
        <div className="portfolio-info-body-wrap">
          <header className="portfolio-info-header">
            <div className="portfolio-info-header__left">Information</div>
            <div className="portfolio-info-header__right">
              <span className="portfolio-info-header__cv">CV</span>
              <button type="button" className="portfolio-link portfolio-info-close" onClick={() => navigate('/portfolio')} aria-label="Close information">
                ×
              </button>
            </div>
          </header>

          <div className="portfolio-info-columns">
          <div className="portfolio-info-column portfolio-info-column--left" aria-hidden="true" />
          <div className="portfolio-info-column portfolio-info-column--right">
            <div className="portfolio-info-body">
              <section className="portfolio-info-row portfolio-info-row--profile">
                <div className="portfolio-info-row__label">
                  <p className="portfolio-info-profile-name">{cv.profile.name}</p>
                  <p>{cv.profile.label}</p>
                  {cv.profile.contact.map((item) => (
                    <PortfolioInfoContact key={item.text} item={item} />
                  ))}
                </div>
                <div className="portfolio-info-row__content">
                  {cv.profile.intro.map((paragraph, index) => (
                    <p key={`intro-${index}`}>{paragraph}</p>
                  ))}
                </div>
              </section>

              {cv.sections.map((section) => (
                <section key={section.label} className="portfolio-info-row">
                  <div className="portfolio-info-row__label">{section.label}</div>
                  <div className="portfolio-info-row__content">
                    <PortfolioInfoSectionContent section={section} />
                  </div>
                </section>
              ))}

              {cv.lastUpdated && (
                <p className="portfolio-info-updated">Last Updated {cv.lastUpdated}</p>
              )}
            </div>
          </div>
        </div>
        </div>

        <footer className="portfolio-info-footer">
          <span>{footerName}</span>
          <button type="button" className="portfolio-link" onClick={() => navigate('/portfolio')}>Projects</button>
          <span className="portfolio-info-footer__right">Information</span>
        </footer>
      </div>
    </div>
  );
}

export default function PortfolioPage() {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const pathname = location.pathname.replace(/\/$/, '');
  const isInformation = pathname.endsWith('/information');
  const isIndex = pathname.endsWith('/index');
  const isProject = pathname.includes('/project/');
  const fromIndex = location.state?.from === 'index';
  const showMain = !isInformation && !isIndex && !(isProject && fromIndex);
  const showIndex = isIndex || (isProject && fromIndex);

  useEffect(() => {
    document.documentElement.classList.add('portfolio-route');
    return () => document.documentElement.classList.remove('portfolio-route');
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const ts = Date.now();
        const [projectsResponse, cvResponse] = await Promise.all([
          fetch(`${API_BASE}/api/portfolio/projects?ts=${ts}`, { cache: 'no-store' }),
          fetch(`${API_BASE}/api/portfolio/cv?ts=${ts}`, { cache: 'no-store' })
        ]);
        if (!mounted) return;

        if (!projectsResponse.ok) {
          throw new Error(`Projects request failed (${projectsResponse.status})`);
        }

        const projectsRes = await projectsResponse.json();
        const cvRes = cvResponse.ok ? await cvResponse.json() : { success: false };

        if (projectsRes.success) {
          setProjects(projectsRes.projects || []);
          setLoadError('');
        } else {
          setLoadError(projectsRes.error || 'Failed to load portfolio projects');
        }

        if (cvRes.success) setCv(cvRes.cv || null);
      } catch (error) {
        console.error('Failed loading portfolio data:', error);
        if (mounted) {
          setLoadError(error.message || 'Failed to load portfolio projects');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    const intervalId = window.setInterval(load, 5000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const useDemoPreview = process.env.NODE_ENV === 'development' && projects.length === 0;
  const displayProjects = useDemoPreview ? DEMO_PROJECTS : projects;
  const displayCv = hasSavedCv(cv) ? cv : defaultCv;
  const profileName = displayCv.profile?.name || defaultCv.profile.name;

  return (
    <>
      {showMain && (
        <PortfolioMain
          projects={displayProjects}
          profileName={profileName}
          loading={loading}
          loadError={loadError}
        />
      )}
      {showIndex && (
        <PortfolioIndexView
          projects={displayProjects}
          profileName={profileName}
          loading={loading}
          loadError={loadError}
        />
      )}
      <Routes>
        <Route path="index" element={null} />
        <Route path="information" element={<PortfolioInfo cv={displayCv} />} />
        <Route path="project/:slug" element={<ProjectSlidePanel projects={displayProjects} />} />
      </Routes>
    </>
  );
}
